<script setup lang="ts">
import CalculationSideBar from '@/components/calculation-form/CalculationSideBar.vue'
import { useCalculationStore } from '@/stores/calculation'
import { storeToRefs } from 'pinia'
import { computed, markRaw, onMounted, ref, onUnmounted, watch } from 'vue'
import type {
  CalculationFormTab,
  CalculationFormTabs
} from '@/components/calculation-form/type/type'
import TransportConfiguration from '@/components/calculation-form/transport-configuration/TransportConfiguration.vue'
import CalculationData from '@/components/calculation-form/CalculationData.vue'
import BuildingBlockSelection from '@/components/calculation-form/building-block-selection/BuildingBlockSelection.vue'
import DesignLinks from '@/components/calculation-form/design-link/DesignLinks.vue'
import EnvironmentDatabaseTab from '@/components/calculation-form/environmental-database/EnvironmentDatabaseTab.vue'
import BuildingBlockView from '@/views/building_blocks/BuildingBlockView.vue'
import { useRoute } from 'vue-router'
import { STATUS_CHOICES } from '@/components/calculation-form/constants/constants'
import { isDatabaseSwitchRecoveryTab } from '@/components/calculation-form/constants/databaseSwitchRecoveryTabs'
import { useQuantityBuildingBlockStore } from '@/stores/quantityBuildingBlock'
import { useBuildingBlockStore } from '@/stores/buildingBlock'
import { useTransportConfigStore } from '@/stores/transportConfig'
import { useEnvironmentDatabaseStore } from '@/stores/environmentDatabase'

const route = useRoute()
const transportConfigStore = useTransportConfigStore()
const buildingBlockStore = useBuildingBlockStore()
const quantityBuildingBlockStore = useQuantityBuildingBlockStore()
const environmentDatabaseStore = useEnvironmentDatabaseStore()

const calculationStore = useCalculationStore()
const {
  currentItem: currentCalculationItem,
  hasMissingDatabaseSwitchIssues: hasMissingDatabaseSwitchIssues
} = storeToRefs(calculationStore)

const calculationId = computed(() => Number(route.params.calculationId))

const tab = ref<CalculationFormTab>('CALCULATION_DATA')
const tabs = ref<CalculationFormTabs>([
  {
    value: 'CALCULATION_DATA',
    label: 'Calculation Data',
    isCompleted: false,
    isShown: true,
    component: markRaw(CalculationData)
  },
  {
    value: 'ENVIRONMENTAL_DATABASE',
    label: 'Environmental Database',
    isCompleted: false,
    isShown: true,
    component: markRaw(EnvironmentDatabaseTab)
  },
  {
    value: 'BUILDING_BLOCKS',
    label: 'Building Blocks',
    isCompleted: false,
    isShown: true,
    component: markRaw(BuildingBlockView)
  },
  {
    value: 'BUILDING_BLOCK_SELECTION',
    label: 'Building Block Selection',
    isCompleted: false,
    isShown: true,
    component: markRaw(BuildingBlockSelection)
  },
  {
    value: 'DESIGN_LINKS',
    label: 'Design Links',
    isCompleted: false,
    isShown: true,
    component: markRaw(DesignLinks)
  },
  {
    value: 'TRANSPORT_CONFIGURATION',
    label: 'Transport Configuration',
    isCompleted: false,
    isShown: true,
    component: markRaw(TransportConfiguration)
  }
])

const updateTabCompletion = (value: CalculationFormTab, isCompleted: boolean) => {
  const tabToUpdate = tabs.value.find((tab) => tab.value === value)
  if (!tabToUpdate) return
  tabToUpdate.isCompleted = isCompleted
}

onMounted(async () => {
  if (!currentCalculationItem.value) {
    try {
      await Promise.all([
        calculationStore.fetchOptions(),
        transportConfigStore.fetchOptions?.(),
        calculationStore.fetchItem(calculationId.value),
        buildingBlockStore.fetchItems({ calculation: calculationId.value }),
        quantityBuildingBlockStore.fetchItems({ calculation: calculationId.value })
      ])
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }
})

onUnmounted(() => {
  calculationStore.resetAll()
  buildingBlockStore.reset()
  quantityBuildingBlockStore.reset()
})

const isReadOnly = computed(() => {
  return (
    currentCalculationItem.value?.status !== STATUS_CHOICES.DRAFT ||
    !calculationStore.canChangeCalculation
  )
})

const isReadOnlyByTab = (tabValue: CalculationFormTab) => {
  if (isReadOnly.value) return true
  if (!hasMissingDatabaseSwitchIssues.value) return false
  return !isDatabaseSwitchRecoveryTab(tabValue)
}

watch(hasMissingDatabaseSwitchIssues, (hasBlockingIssues) => {
  if (!hasBlockingIssues) return
  if (isDatabaseSwitchRecoveryTab(tab.value)) return
  tab.value = 'BUILDING_BLOCKS'
})

watch(tab, async (newTab) => {
  if (newTab === 'DESIGN_LINKS') {
    await Promise.all([
      buildingBlockStore.fetchItems({ calculation: calculationId.value }),
      quantityBuildingBlockStore.fetchItems({ calculation: calculationId.value })
    ])
    return
  }

  if (newTab === 'ENVIRONMENTAL_DATABASE') {
    const projectId = currentCalculationItem.value?.project ?? Number(route.params.id)
    await environmentDatabaseStore.fetchItems({
      project: projectId,
      exclude_calculation: calculationId.value
    })
  }
})
</script>

<template>
  <CalculationSideBar
    v-model:tab="tab"
    :tabs="tabs"
    :has-blocking-switch-issues="hasMissingDatabaseSwitchIssues"
  />

  <template v-if="!currentCalculationItem">
    <div class="d-flex justify-center align-center fill-height pa-6">
      <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
    </div>
  </template>

  <v-window v-else v-model="tab" class="px-8 py-6">
    <v-window-item v-for="item in tabs" :key="item.value" :value="item.value">
      <component
        :is="item.component"
        :tab="item.value"
        :disabled="isReadOnlyByTab(item.value)"
        :readonly="isReadOnlyByTab(item.value)"
        :update-tab-completion="updateTabCompletion"
      >
      </component>
    </v-window-item>
  </v-window>
</template>

<style scoped></style>
